import { useState } from 'react';
import { errorMessage } from '../api/client';
import { eventsApi, newEventId } from '../api/endpoints';
import type { PartItem, PartReference } from '../api/types';
import { fromDateTimeInput, nowForInput } from '../lib/format';
import { Field } from './Field';
import { Modal } from './Modal';
import { Notice } from './Notice';

/**
 * Régularisation d'une anomalie de stock, après inventaire physique.
 *
 * C'est un événement du journal de plus, pas une correction de champ. Il ne
 * réécrit rien et ne supprime aucun mouvement : il ajoute un constat daté,
 * attribué et motivé, et c'est ce constat qui devient la nouvelle base du
 * calcul. Les mouvements postérieurs s'y appliquent.
 *
 * Conséquence à connaître, et dite à l'écran : une régularisation ne corrige
 * pas ce qui s'est passé avant elle, elle repart de ce qu'on a compté. Si un
 * mouvement fautif est annulé plus tard, le stock bougera de nouveau — ce qui
 * est juste, puisque le journal aura changé.
 */
export function StockRegularisationModal({
  reference,
  items,
  onClose,
  onSaved,
}: {
  reference: PartReference;
  items: PartItem[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const parExemplaire = reference.tracking === 'exemplaire';

  const [occurredAt, setOccurredAt] = useState(nowForInput());
  const [reason, setReason] = useState('');
  const [quantite, setQuantite] = useState(String(Math.max(reference.stock, 0)));
  const [itemId, setItemId] = useState(items[0]?.id ?? '');
  const [presence, setPresence] = useState<'present' | 'absent'>('absent');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exemplaire = items.find((item) => item.id === itemId) ?? null;

  const submit = (): void => {
    setError(null);

    if (reason.trim() === '') {
      setError('Le motif est obligatoire : il dit ce qui a été constaté, et par quel moyen.');
      return;
    }

    const payload: Record<string, unknown> = parExemplaire
      ? { exemplaireId: itemId, presence }
      : { referenceId: reference.id, quantiteConstatee: Number.parseInt(quantite, 10) };

    if (!parExemplaire && !Number.isInteger(payload.quantiteConstatee)) {
      setError('La quantité constatée doit être un nombre entier, zéro compris.');
      return;
    }
    if (parExemplaire && itemId === '') {
      setError('Choisissez l’exemplaire sur lequel porte le constat.');
      return;
    }

    setSaving(true);
    eventsApi
      .append({
        id: newEventId(),
        // une régularisation ne concerne aucun poste : le stock n'est sur aucun
        workstationId: null,
        type: 'regularisationStock',
        occurredAt: fromDateTimeInput(occurredAt),
        reason: reason.trim(),
        payload,
      })
      .then(onSaved)
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      title={`Régulariser le stock — ${reference.designation}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="bouton bouton--fantome" onClick={onClose}>
            Annuler
          </button>
          <button
            type="button"
            className="bouton bouton--primaire"
            onClick={submit}
            disabled={saving}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer le constat'}
          </button>
        </>
      }
    >
      <div className="pile pile--serree">
        <Notice tone="info" title="Ce constat s’ajoute, il n’efface rien">
          Aucun mouvement n’est supprimé et la quantité déclarée ne bouge pas. Le
          constat devient la nouvelle base du calcul, et les mouvements postérieurs
          s’y appliquent. Il s’annule comme n’importe quel autre événement, et le
          calcul revient alors à la base précédente.
        </Notice>

        {error !== null && <Notice tone="erreur">{error}</Notice>}

        {parExemplaire ? (
          <>
            <Field
              label="Exemplaire"
              hint="Le constat porte sur la présence ou l’absence de cet exemplaire précis."
            >
              <select value={itemId} onChange={(e) => setItemId(e.target.value)}>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.serialNumber ?? 'sans numéro de série'}
                    {item.workstationName !== null && ` — monté sur ${item.workstationName}`}
                    {!item.present && ' — déjà constaté absent'}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Ce que l’inventaire physique a constaté">
              <select
                value={presence}
                onChange={(e) => setPresence(e.target.value as 'present' | 'absent')}
              >
                <option value="absent">Absent — introuvable</option>
                <option value="present">Présent — retrouvé</option>
              </select>
            </Field>

            {exemplaire !== null && exemplaire.workstationName !== null && (
              <Notice tone="alerte">
                Cet exemplaire est déclaré monté sur {exemplaire.workstationName}. Le
                constat d’absence ne le démonte pas : il dit qu’il n’est plus là, pas
                où il était. Si la pièce a été retirée, enregistrez aussi le retrait
                depuis la fiche du poste.
              </Notice>
            )}
          </>
        ) : (
          <Field
            label="Quantité constatée en réserve"
            hint={`Le journal en annonce ${reference.stock}. Zéro est une valeur valable.`}
          >
            <input
              type="number"
              min={0}
              value={quantite}
              onChange={(e) => setQuantite(e.target.value)}
            />
          </Field>
        )}

        <Field
          label="Date du constat"
          hint="Celle de l’inventaire physique, pas celle de la saisie."
        >
          <input
            type="datetime-local"
            value={occurredAt}
            onChange={(e) => setOccurredAt(e.target.value)}
          />
        </Field>

        <Field
          label="Motif"
          hint="Obligatoire. Ce qui a été compté, par qui, et comment — c'est ce qui distingue un inventaire d'une saisie au hasard."
          wide
        >
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Inventaire physique du 11 septembre, comptage en réserve par…"
          />
        </Field>
      </div>
    </Modal>
  );
}
